import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import useColumnConfig, { getStoredPageSize } from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import { Alert, Button, Card, Popconfirm, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, TableOutlined, DeleteOutlined, RedoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getAirtableErrors,
  deleteAirtableLog,
  deleteAllAirtableErrors,
  deleteAirtableLogsByIds,
  resendOrderAirtable,
} from '../modules/airtable-logs/api';

const { Paragraph } = Typography;

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm:ss') : '—');

/**
 * OVO-227 — Monitoring → Airtable sync errors. Cross-order list of Airtable PATCH
 * attempts that ended FAILED (429 rate-limit, 10s timeout, or a 4xx/5xx). Each row
 * can be resent (idempotently re-pushes the order's current status) or deleted.
 */
const AirtableSyncErrorsPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Seeded from the Settings drawer's stored value so the first fetch and the
  // table agree; the drawer keeps both in step afterwards.
  const [pageSize, setPageSize] = useState(() => getStoredPageSize('airtable-errors-columns', 50));
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // Server-side filters — same reasoning as the webhook errors page.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [resendingId, setResendingId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(
    async (p = page, ps = pageSize, f = filters) => {
      setLoading(true);
      try {
        const { response } = await getAirtableErrors({ take: ps, skip: (p - 1) * ps, ...f });
        setRows(response?.items || []);
        setTotal(response?.total || 0);
      } catch (e) {
        messageApi.error(e?.message || t('common:error'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, filters],
  );

  useEffect(() => {
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadKeepingPage = (removed = 1) => {
    const nextTotal = Math.max(0, total - removed);
    const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));
    const targetPage = Math.min(page, lastPage);
    setPage(targetPage);
    load(targetPage, pageSize);
  };

  const handleResend = async (rec) => {
    setResendingId(rec.id);
    try {
      const { response } = await resendOrderAirtable(rec.orderCode);
      const outcome = response?.outcome;
      if (outcome === 'synced') {
        messageApi.success(t('airtableErrors.resent', { code: rec.orderCode }));
        setSelectedRowKeys((keys) => keys.filter((k) => k !== rec.id));
        reloadKeepingPage(1);
      } else if (outcome === 'skipped') {
        messageApi.info(t('airtableErrors.resendSkipped'));
      } else {
        messageApi.error(t('airtableErrors.resendFailed'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { response } = await deleteAirtableLog(id);
      if (response?.success) {
        messageApi.success(t('airtableErrors.deleted'));
        setSelectedRowKeys((keys) => keys.filter((k) => k !== id));
        reloadKeepingPage(1);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    }
  };

  const handleDeleteAll = async () => {
    try {
      const { response } = await deleteAllAirtableErrors();
      if (response?.success) {
        messageApi.success(t('airtableErrors.deletedAll', { count: response.count ?? 0 }));
        setSelectedRowKeys([]);
        setPage(1);
        load(1, pageSize);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      const { response } = await deleteAirtableLogsByIds(selectedRowKeys);
      if (response?.success) {
        messageApi.success(t('airtableErrors.deletedAll', { count: response.count ?? selectedRowKeys.length }));
        const removed = response.count ?? selectedRowKeys.length;
        setSelectedRowKeys([]);
        reloadKeepingPage(removed);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    }
  };

  const statusTag = (rec) => {
    if (rec.responseStatus == null) {
      return <Tag color="red">{t('airtableErrors.noResponse')}</Tag>;
    }
    return <Tag color="red">{rec.responseStatus}</Tag>;
  };

  const columns = [
    {
      title: t('airtableErrors.time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 175,
      render: fmtTime,
    },
    {
      title: t('airtableErrors.order'),
      key: 'order',
      width: 130,
      render: (_, rec) => (rec.orderCode ? <Link to={`/admin/orders/${rec.orderCode}`}>{rec.orderCode}</Link> : '—'),
    },
    {
      title: t('airtableErrors.status'),
      key: 'airtableStatus',
      width: 130,
      render: (_, rec) => (rec.airtableStatus ? <Tag>{rec.airtableStatus}</Tag> : '—'),
    },
    {
      title: t('airtableErrors.response'),
      key: 'responseStatus',
      width: 110,
      render: (_, rec) => statusTag(rec),
    },
    {
      title: t('airtableErrors.errorMessage'),
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span style={{ color: '#cf1322' }}>{v.length > 80 ? `${v.slice(0, 80)}…` : v}</span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_, rec) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Button
            type="link"
            size="small"
            icon={<RedoOutlined />}
            loading={resendingId === rec.id}
            onClick={() => handleResend(rec)}
          >
            {t('airtableErrors.resend')}
          </Button>
          <Popconfirm
            title={t('airtableErrors.deleteConfirm')}
            okText={t('common:delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:cancel')}
            onConfirm={() => handleDelete(rec.id)}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const expanded = (rec) => (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px', minWidth: 280 }}>
        <b>{t('airtableErrors.request')}</b>
        <Paragraph code copyable style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
          {typeof rec.requestBody === 'string' ? rec.requestBody : JSON.stringify(rec.requestBody, null, 2)}
        </Paragraph>
      </div>
      <div style={{ flex: '1 1 320px', minWidth: 280 }}>
        <b>{t('airtableErrors.response')}</b>
        <Paragraph code copyable style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
          {rec.responseBody || rec.errorMessage || '—'}
        </Paragraph>
      </div>
    </div>
  );

  // Same Settings drawer as every other list: it owns column visibility/order
  // AND the page size, so the bottom "N / page" selector is dropped and there
  // is a single source of truth. Changing the size refetches from page 1,
  // because this list is paginated server-side.
  const FIXED_KEYS = ['actions'];
  const columnConfig = useColumnConfig('airtable-errors-columns', columns, FIXED_KEYS);

  return (
    <div className="AirtableSyncErrorsPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <TableOutlined style={{ color: '#cf1322', marginRight: 8 }} />
              {t('airtableErrors.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 720, marginTop: 4 }}>
              {t('airtableErrors.subtitle')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={t('airtableErrors.deleteSelectedConfirm', { count: selectedRowKeys.length })}
                okText={t('common:delete')}
                okButtonProps={{ danger: true }}
                cancelText={t('common:cancel')}
                onConfirm={handleDeleteSelected}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('airtableErrors.deleteSelected', { count: selectedRowKeys.length })}
                </Button>
              </Popconfirm>
            )}
            {total > 0 && (
              <Popconfirm
                title={t('airtableErrors.deleteAllConfirm', { count: total })}
                okText={t('common:delete')}
                okButtonProps={{ danger: true }}
                cancelText={t('common:cancel')}
                onConfirm={handleDeleteAll}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('airtableErrors.deleteAll')}
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={() => load(page, pageSize)}>
              {t('airtableErrors.refresh')}
            </Button>
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            <Button
              type="text"
              title="Settings"
              onClick={columnConfig.openDrawer}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        <Spin spinning={loading}>
          {rows.length === 0 && !loading ? (
            <Alert type="success" showIcon message={t('airtableErrors.allClear')} />
          ) : (
            <Table
              rowKey="id"
              size="small"
              columns={columnConfig.visibleColumns}
              dataSource={rows}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
              }}
              expandable={{ expandedRowRender: expanded }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                onChange: (p, ps) => {
                  setSelectedRowKeys([]);
                  setPage(p);
                  setPageSize(ps);
                  load(p, ps);
                },
              }}
            />
          )}
        </Spin>
        <TableFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          values={filters}
          onApply={(f) => {
            setFilters(f);
            setSelectedRowKeys([]);
            setPage(1);
            load(1, pageSize, f);
          }}
          fields={[
            { name: 'orderCode', label: t('airtableErrors.order'), type: 'text', placeholder: 'P…' },
            {
              name: 'syncType',
              label: t('airtableErrors.syncType', 'Sync type'),
              placeholder: '—',
              options: [
                { value: 'status', label: 'status' },
                { value: 'arriving_soon', label: 'arriving_soon' },
              ],
            },
          ]}
        />
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={(size) => {
            columnConfig.setPageSize(size);
            setPageSize(size);
            setPage(1);
            load(1, size);
          }}
        />
      </Card>
    </div>
  );
};

export default AirtableSyncErrorsPage;
